import Common from '../plugins/Common';
import axios, { AxiosResponse } from 'axios';
import { BillingAddressDetails, PaymentRequest, useAppContext, usePaymentContext } from '../context';

export function useRequestManager() {
    const appContext = useAppContext();
    const paymentContext = usePaymentContext();

    const getPaymentRequest = (paymentRequestId: string): Promise<PaymentRequest> => {
        const url = `${window.__appSettings.apiEndpoints.opayoPaymentRequestApi}/${paymentRequestId}`;
        return axios.get<PaymentRequest>(url)
            .then(response => {
                checkResponse(url, response);
                return response.data;
            });
    };

    const getOpayoTransactionUrl = (paymentRequestId: string, billingAddress: BillingAddressDetails): Promise<string> => {
        if (window.__appSettings.isDevelopmentMode ?? false)
            return Promise.resolve('http://localhost:20102/HTML/card_details_low.html');

        const url = `${window.__appSettings.apiEndpoints.opayoTransactionApi}/${paymentRequestId}`;
        return axios.post<string>(url, billingAddress)
            .then(response => {
                checkResponse(url, response);
                return response.data;
            });
    };

    const checkResponse = <T>(url: string, response: AxiosResponse<T>): boolean => {
        if (response.status >= 200 && response.status <= 299) {
            console.info(`Call to ${url} was successful`);
            return true;
        }

        throw new Error(`Failed to call ${url} (Status: ${response.status})`);
    };

    const loadRequest = (paymentRequestId: string, shouldNavigate: boolean): Promise<void> => {
        if (Common.isNullOrWhiteSpace(paymentRequestId)) {
            paymentContext.setPaymentRequest();
            appContext.navigateToLandingPage(); // no ID, send back to the landing page
            return Promise.resolve();
        }

        if (appContext.isLoading) {
            return Promise.resolve();
        }

        if (paymentContext.paymentRequest?.shortId === paymentRequestId) {
            return Promise.resolve();
        }

        appContext.setIsLoading(true);
        paymentContext.setHasInvalidCode(false);

        return getPaymentRequest(paymentRequestId)
            .then((paymentRequest) => {
                paymentContext.setPaymentRequest(paymentRequest);

                if (paymentRequest.hasExpired)
                    return appContext.navigateToPaymentExpiredPage(paymentRequest.shortId);

                if (paymentRequest.isPaid)
                    return appContext.navigateToPaymentCompletePage(paymentRequest.shortId);

                if (shouldNavigate)
                    return appContext.navigateToAddressPage(paymentRequest.shortId);
            })
            .catch((e) => {
                console.error('Failed to load payment request', e);
                paymentContext.setPaymentRequest();
                paymentContext.setHasInvalidCode(true);

                // send back to the landing page
                appContext.navigateToLandingPage();
                return;
            })
            .then(() => appContext.setIsLoading(false));
    };

    const createTransaction = (billingAddress: BillingAddressDetails): Promise<boolean> => {
        if (!paymentContext.paymentRequest) {
            appContext.navigateToLandingPage(); // no payment request loaded, send back to the landing page
            return Promise.resolve(false);
        }

        const requestId = paymentContext.paymentRequest.requestId;
        const shortId = paymentContext.paymentRequest.shortId;

        paymentContext.setBillingAddressDetails(billingAddress);
        paymentContext.setHasBillingAddress(true);

        const host = `https://${window.location.host}`;
        billingAddress.onFailureReturnUrl = `${host}/${requestId}/payment/error`;
        billingAddress.onSuccessReturnUrl = `${host}/${requestId}/payment/complete`;

        appContext.setIsLoading(true);

        return getOpayoTransactionUrl(requestId, billingAddress)
            .then((paymentRequest) => {
                paymentContext.setTransactionUrl(paymentRequest);
                appContext.navigateToPaymentPage(shortId);

                return true;
            })
            .catch((_) => {
                paymentContext.setHasBillingAddress(false);
                paymentContext.setHasBillingAddressError(true);

                // send back to the address page
                appContext.navigateToAddressPage(shortId);
                return false;
            })
            .then((result) => {
                appContext.setIsLoading(false);
                return result;
            });
    };

    return {
        loadRequest,
        createTransaction,
    } as const;
}
